import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as LinkIcon } from '../../../assets/icons/link.svg';
import { ReactComponent as NextArrowIcon } from '../../../assets/icons/next-arrow.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as ReconnectedIcon } from '../../../assets/icons/reconnect.svg';
import { ReactComponent as Settings } from '../../../assets/icons/setting-icon.svg';
import { ReactComponent as FacilityIcon } from '../../../assets/icons/sidebar/sidebar-facility.svg';
import { ReactComponent as SuspendedIcon } from '../../../assets/icons/suspended.svg';
import { ReactComponent as TagIcon } from '../../../assets/icons/tag.svg';
import { ReactComponent as UserIcon } from '../../../assets/icons/user-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { ApiRequest } from '../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  // authorizeOcpiConnection,
  createOcpiTenant,
  deleteOcpiConnection,
  deleteOcpiTenant,
  reconnectOcpiConnection,
  suspendOcpiConnection,
  disableFiltersApplied,
  fetchOcpiConnectionFilters,
  fetchOcpiConnections,
  fetchOcpiRoles,
  setSelectedRole,
  setSortData,
  updateConnectionFilters,
} from '../../../redux/slices/OCPI/OCPIConnectionsSlice';
import { RootState } from '../../../redux/store';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { ISO_COUNTRY_CODES, isValidCountryCode } from '../../../shared-functions/CountryCodes';
import { platformPermissionList } from '../../../shared-functions/JWTDocodeFunction';
import { getLocalStorageItem } from '../../../shared-functions/LocalStorage';

import AdditionalFieldsModal from './AdditionalFieldsModal';
import ConnectionSidePane from './ConnectionSidePane';
import RegisterPartnerModal from './RegisterPartnerModal';

/**
 * OCPI Roaming Connections component (Split Layout).
 * Matches the wireframe with Roles on the left and Connections on the right.
 * @returns {any} The rendered component.
 */
function OCPIConnections(): JSX.Element {
  const { t } = useTranslation(['ocpi', 'sharedComponent']);
  const domain = getLocalStorageItem('client-name');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { activePortal } = usePortal();
  const { activeProduct: reduxActiveProduct } = useAppSelector((state: any) => state.product);
  const activeProduct =
    reduxActiveProduct || (activePortal === PortalType.MOBILITY ? 'EMSP' : 'CPO');

  const {
    tenants,
    limit,
    connections,
    selectedRole,
    loadingRoles,
    loadingConns,
    filters,
    filterListLoading,
    isFilterUpdated,
    page,
    totalPages,
    totalResults,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.ocpiConnections);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  // Modals & Panels
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [newPartyId, setNewPartyId] = useState('');
  const [newCountryCode, setNewCountryCode] = useState('');
  const [formErrors, setFormErrors] = useState<{
    partyId?: string;
    countryCode?: string;
    role?: string;
  }>({});

  // Delete Modals State
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<any>(null);
  const [showDeleteConnectionModal, setShowDeleteConnectionModal] = useState(false);
  const [connectionSuspend, setShowConnectionSuspendModal] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchOcpiRoles());
  }, [activeProduct, dispatch]);

  // Auto-select the role matching the active product once the tenant list finishes loading.
  // Connections must not be fetched before a tenant is confirmed to exist for the role,
  // otherwise the backend rejects the request with "No active tenant found for tenantId".
  useEffect(() => {
    if (loadingRoles || tenants.length === 0) return;
    const activeTenant = tenants.find(
      (tenant: any) => tenant.role?.toUpperCase() === activeProduct?.toUpperCase(),
    );
    const tenantToSelect = activeTenant || tenants[0];
    dispatch(setSelectedRole(tenantToSelect));
    dispatch(fetchOcpiConnections({ role: tenantToSelect.role }));
  }, [tenants, activeProduct, dispatch]);

  // Helper to check if both sender and receiver are local tenants
  /**
   * Checks if a connection is a loopback connection between local tenants.
   * @param {any} c - Connection object.
   * @returns {boolean} True if both sender and receiver are local tenants.
   */
  const isLoopbackConnection = (c: any) => {
    const senderIsLocal = tenants.some(
      (tenant: any) =>
        tenant.role?.toUpperCase() === c.senderRole?.toUpperCase() &&
        tenant.partyId?.toUpperCase() === c.senderPartyId?.toUpperCase() &&
        tenant.countryCode?.toUpperCase() === c.senderCountryCode?.toUpperCase(),
    );
    const receiverIsLocal = tenants.some(
      (tenant: any) =>
        tenant.role?.toUpperCase() === c.role?.toUpperCase() &&
        tenant.partyId?.toUpperCase() === c.partyId?.toUpperCase() &&
        tenant.countryCode?.toUpperCase() === c.countryCode?.toUpperCase(),
    );
    return senderIsLocal && receiverIsLocal;
  };

  /**
   * Function to handle connection suspend
   * @param {any} rowData - The rowData
   */
  const handleConnectionSuspend = (rowData: ConnectionRow) => {
    setConnectionToDelete(rowData);
    setShowConnectionSuspendModal(true);
  };

  // Filter connections based on the currently selected tenant (we could be either Sender or Receiver)
  const displayedConnections = selectedRole
    ? connections.filter((c) => {
        const isSenderMatch =
          c.senderRole?.toUpperCase() === selectedRole.role?.toUpperCase() &&
          c.senderPartyId?.toUpperCase() === selectedRole.partyId?.toUpperCase() &&
          c.senderCountryCode?.toUpperCase() === selectedRole.countryCode?.toUpperCase();

        const isReceiverMatch =
          c.role?.toUpperCase() === selectedRole.role?.toUpperCase() &&
          c.partyId?.toUpperCase() === selectedRole.partyId?.toUpperCase() &&
          c.countryCode?.toUpperCase() === selectedRole.countryCode?.toUpperCase();

        const loopback = isLoopbackConnection(c);
        if (loopback) {
          // For loopback connection, only show it when selectedRole is the sender
          return isSenderMatch;
        }

        return isSenderMatch || isReceiverMatch;
      })
    : connections;

  // Apply the table's search + status filters (client-side; OCPI connections are fetched in full)
  const activeSearchFilter = filters.find((filter) => filter.searchBy);
  const activeStatusFilter = filters.find((filter) => filter.key === 'status');
  const searchTerm = (activeSearchFilter?.applyFilter ? activeSearchFilter.value : '')
    .toString()
    .toLowerCase();
  const statusValue = activeStatusFilter?.applyFilter ? activeStatusFilter.value.toString() : '';

  const filteredConnections = displayedConnections.filter((conn) => {
    const activeRoleName = selectedRole?.role || activeProduct;
    const isWeSender = conn.senderRole?.toUpperCase() === activeRoleName?.toUpperCase();
    const partnerRole = isWeSender ? conn.role : conn.senderRole;

    if (statusValue && (conn.status || 'PENDING') !== statusValue) {
      return false;
    }

    if (!searchTerm) return true;
    return (
      (partnerRole || '').toLowerCase().includes(searchTerm) ||
      (conn.status || '').toLowerCase().includes(searchTerm)
    );
  });

  // Read live from redux (rather than snapshotting at click time) so the modal reflects
  // status changes made via authorize/delete without needing to be reopened.
  const selectedConnection = selectedConnectionId
    ? connections.find((c: any) => (c._id || c.id) === selectedConnectionId)
    : null;

  // Derive available OCPI roles from JWT platformPermissions (e.g. ['networks', 'mobility', ...])
  // instead of the never-populated Redux availableProducts state.
  const platformPerms = platformPermissionList();
  const PLATFORM_TO_OCPI_ROLE: Record<string, string> = {
    networks: 'CPO',
    mobility: 'EMSP',
  };
  const allOcpiRoles = platformPerms
    .filter((p: string) => PLATFORM_TO_OCPI_ROLE[p])
    .map((p: string) => PLATFORM_TO_OCPI_ROLE[p]);

  const availableRoleOptions = allOcpiRoles.length > 0 ? allOcpiRoles : ['CPO', 'EMSP'];

  /**
   * Open add role modal and initialize values
   */
  const openAddRoleModal = () => {
    setNewPartyId('');
    setNewCountryCode('');
    setNewRole(availableRoleOptions[0] || '');
    setFormErrors({});
    setIsAddRoleModalOpen(true);
  };

  /**
   * Closes the add-role modal and clears its form fields.
   */
  const closeAddRoleModal = () => {
    setIsAddRoleModalOpen(false);
    setNewRole('');
    setNewPartyId('');
    setNewCountryCode('');
    setFormErrors({});
  };

  /**
   * Validates Party ID input and returns whether it is valid.
   * Must be exactly 3 alphanumeric characters.
   * @param {string} value - The Party ID string.
   * @returns {boolean} True if valid, false otherwise.
   */
  const validatePartyId = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setFormErrors((previous) => ({ ...previous, partyId: t('ocpi:enterPartyIdError') }));
      return false;
    }
    if (trimmed.length !== 3 || !/^[A-Z0-9]{3}$/i.test(trimmed)) {
      setFormErrors((previous) => ({ ...previous, partyId: t('ocpi:partyIdLengthError') }));
      return false;
    }
    setFormErrors((previous) => ({ ...previous, partyId: '' }));
    return true;
  };

  /**
   * Validates Country Code input against ISO 3166-1 alpha-2 values and returns whether it is valid.
   * @param {string} value - The Country Code string.
   * @returns {boolean} True if valid, false otherwise.
   */
  const validateCountryCode = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) {
      setFormErrors((previous) => ({ ...previous, countryCode: t('ocpi:enterCountryCodeError') }));
      return false;
    }
    if (trimmed.length !== 2) {
      setFormErrors((previous) => ({ ...previous, countryCode: t('ocpi:countryCodeLengthError') }));
      return false;
    }
    if (!isValidCountryCode(trimmed)) {
      setFormErrors((previous) => ({
        ...previous,
        countryCode: t('ocpi:invalidCountryCodeError'),
      }));
      return false;
    }
    setFormErrors((previous) => ({ ...previous, countryCode: '' }));
    return true;
  };

  /**
   * Handles Party ID value change and resets error when valid.
   * Restricts input to 3 alphanumeric characters.
   * @param {string} value - The input value.
   */
  const handlePartyIdChange = (value: string) => {
    const formattedValue = value
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .slice(0, 3);
    setNewPartyId(formattedValue);
    if (formErrors.partyId && formattedValue.trim().length === 3) {
      setFormErrors((previous) => ({ ...previous, partyId: '' }));
    }
  };

  /**
   * Handles Country Code value change and resets error when valid.
   * @param {string} value - The input value.
   */
  const handleCountryCodeChange = (value: string) => {
    const upperValue = value.trim().toUpperCase();
    setNewCountryCode(upperValue);
    if (formErrors.countryCode && isValidCountryCode(upperValue)) {
      setFormErrors((previous) => ({ ...previous, countryCode: '' }));
    }
  };

  /**
   * Handles adding a new role by validating input and dispatching createOcpiTenant.
   * @returns {Promise<void>} Resolves when role creation attempt finishes.
   */
  const handleAddRole = async () => {
    let firstErrorMessage = '';

    if (!newRole) {
      setFormErrors((previous) => ({ ...previous, role: t('ocpi:selectRoleError') }));
      firstErrorMessage = t('ocpi:selectRoleError');
    }

    const isPartyIdValid = validatePartyId(newPartyId);
    if (!isPartyIdValid && !firstErrorMessage) {
      firstErrorMessage = !newPartyId.trim()
        ? t('ocpi:enterPartyIdError')
        : t('ocpi:partyIdLengthError');
    }

    const isCountryCodeValid = validateCountryCode(newCountryCode);
    if (!isCountryCodeValid && !firstErrorMessage) {
      if (!newCountryCode.trim()) {
        firstErrorMessage = t('ocpi:enterCountryCodeError');
      } else if (newCountryCode.trim().length !== 2) {
        firstErrorMessage = t('ocpi:countryCodeLengthError');
      } else {
        firstErrorMessage = t('ocpi:invalidCountryCodeError');
      }
    }

    if (!newRole || !isPartyIdValid || !isCountryCodeValid) {
      if (firstErrorMessage) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: firstErrorMessage,
            type: 'error',
          }),
        );
      }
      return;
    }

    // Client-side uniqueness check
    const isDuplicate = tenants.some(
      (tenant: any) =>
        tenant.role?.toUpperCase() === newRole.toUpperCase() &&
        tenant.partyId?.toUpperCase() === newPartyId.trim().toUpperCase() &&
        tenant.countryCode?.toUpperCase() === newCountryCode.trim().toUpperCase(),
    );

    if (isDuplicate) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('ocpi:duplicateRoleError'),
          type: 'error',
        }),
      );
      return;
    }

    setAddingRole(true);
    try {
      await dispatch(
        createOcpiTenant({
          partyId: newPartyId.trim().toUpperCase(),
          countryCode: newCountryCode.trim().toUpperCase(),
          role: newRole,
          businessDetails: { name: 'Energic Platform', domain },
          backendEndpoint:
            newRole === 'CPO'
              ? process.env.REACT_APP_API_NETWORKS_URL
              : process.env.REACT_APP_API_MOBILITY_URL,
        }),
      ).unwrap();
      setIsAddRoleModalOpen(false);
      setNewRole('');
      setNewPartyId('');
      setNewCountryCode('');
      setFormErrors({});
    } catch {
      // Notification is already dispatched with the exact API error (e.g. statusMessage) by the createOcpiTenant thunk;
      // keep the modal open so the user can see/fix the issue.
    } finally {
      setAddingRole(false);
    }
  };

  /**
   * Handle delete role (tenant) click
   * @param {any} tenant - The tenant
   */
  // const handleDeleteRole = (tenant: any) => {
  //   const roleConns = connections.filter(
  //     (c) =>
  //       c.role?.toUpperCase() === tenant.role?.toUpperCase() ||
  //       c.senderRole?.toUpperCase() === tenant.role?.toUpperCase(),
  //   );

  //   if (roleConns.length > 0) {
  //     dispatch(
  //       setNotificationMessage({
  //         status: true,
  //         message: t('ocpi:cannotDeleteRole', { role: tenant.role }),
  //         type: 'error',
  //       }),
  //     );
  //     return;
  //   }

  //   setRoleToDelete(tenant);
  //   setShowDeleteRoleModal(true);
  // };

  /**
   * Execute actual delete role request.
   * @returns {Promise<void>} Resolves when delete role is complete.
   */
  const executeDeleteRole = async () => {
    if (!roleToDelete) return;
    await dispatch(deleteOcpiTenant(roleToDelete._id || roleToDelete.id));
    setShowDeleteRoleModal(false);
    setRoleToDelete(null);
  };

  /**
   * Execute actual suspend connection request.
   * @returns {Promise<void>} Resolves when suspend connection is complete.
   */
  const executeSuspendConnection = async () => {
    if (!connectionToDelete) return;
    if (connectionToDelete.raw.suspended) {
      await dispatch(reconnectOcpiConnection({ id: connectionToDelete._id || connectionToDelete.id, role: connectionToDelete.raw.senderRole }));
    } else {
      await dispatch(suspendOcpiConnection({ id: connectionToDelete._id || connectionToDelete.id, role: connectionToDelete.raw.senderRole }));
    }
    setShowConnectionSuspendModal(false);
    setConnectionToDelete(null);
  };

  /**
   * Handle delete connection click
   * @param {any} conn - The conn
   */
  const handleDeleteConnection = (conn: any) => {
    setConnectionToDelete(conn);
    setShowDeleteConnectionModal(true);
  };

  /**
   * Execute actual delete connection request. Resolves which side of the connection is
   * "us" (the currently selected tenant) vs the "counter" partner, since the unregister
   * endpoint always terminates the link from our own tenant's perspective.
   * @returns {Promise<void>} Resolves when delete connection is complete.
   */
  const executeDeleteConnection = async () => {
    if (!connectionToDelete || !selectedRole) return;
    const activeRoleName = selectedRole.role || activeProduct;

    await dispatch(
      deleteOcpiConnection({
        role: activeRoleName,
        id: connectionToDelete.id,
      }),
    );
    setShowDeleteConnectionModal(false);
    setConnectionToDelete(null);
    setSelectedConnectionId(null);
  };

  /**
   * Opens the connection details modal for the clicked row.
   * @param {string} id - The connection id.
   */
  const handleOpenConnectionModal = (id: string) => {
    setSelectedConnectionId(id);
  };

  /**
   * Fetches OCPI connections with the given pagination/sort/role parameters.
   * @param {any} data - The page/limit/sort/role parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loadingConns) {
      dispatch(fetchOcpiConnections(data));
    }
  };

  /**
   * Selects a platform role and refetches connections filtered by that role.
   * @param {any} tenant - The tenant (role) being selected.
   */
  const handleSelectRole = (tenant: any) => {
    dispatch(setSelectedRole(tenant));
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      role: tenant.role,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Handle the table's sort change.
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      role: selectedRole?.role,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Handle the table's page/limit change.
   * @param {any} value - The page/limit parameters.
   */
  const pageLimitChange = (value: ApiRequest) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, role: selectedRole?.role, ...value, sortField, sortBy });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, role: selectedRole?.role, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        role: selectedRole?.role,
        ...queryParameters,
      });
      dispatch(disableFiltersApplied());
    }
  }, [filters]);

  /**
   * Render add role content
   * @returns {any} JSX
   */
  const renderAddRoleContent = () => {
    if (availableRoleOptions.length === 0) {
      return (
        <div className="ocpi-add-role-form ocpi-connections__add-role-empty">
          <p>{t('ocpi:noAdditionalRoles')}</p>
          <div className="ocpi-connections__close-btn-wrap">
            <MyButton
              label={t('ocpi:close')}
              buttonType="primary"
              onClickFunc={closeAddRoleModal}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="ocpi-add-role-form">
        <div className="ocpi-connections__field-group">
          <CustomInput
            inputLabel={t('ocpi:partyId')}
            type="text"
            name="newPartyId"
            placeholder={t('ocpi:partyIdPlaceholder')}
            value={newPartyId}
            onChangeValue={(_, value) => handlePartyIdChange(value as string)}
            handleBlur={() => validatePartyId(newPartyId)}
            characterLimit={3}
            error={Boolean(formErrors.partyId)}
            errorMessage={formErrors.partyId}
          />
          {!formErrors.partyId && (
            <span className="ocpi-connections__field-hint">{t('ocpi:partyIdHint')}</span>
          )}
        </div>

        <div className="ocpi-connections__field-group">
          <SelectField
            inputLabel={t('ocpi:countryCode')}
            name="newCountryCode"
            placeholder={t('ocpi:countryCodePlaceholder')}
            options={ISO_COUNTRY_CODES}
            selectedValue={newCountryCode}
            enableSearch
            onChangeValue={(_, value) => handleCountryCodeChange(value as string)}
            error={Boolean(formErrors.countryCode)}
            errorMessage={formErrors.countryCode}
          />
          {!formErrors.countryCode && (
            <span className="ocpi-connections__field-hint">{t('ocpi:countryCodeHint')}</span>
          )}
        </div>

        <div className="ocpi-connections__field-group">
          <SelectField
            inputLabel={t('ocpi:availableRole')}
            name="newRole"
            options={availableRoleOptions.map((r: string) => ({ label: r, value: r }))}
            selectedValue={newRole}
            onChangeValue={(_, value) => {
              setNewRole(value);
              if (formErrors.role) {
                setFormErrors((previous) => ({ ...previous, role: '' }));
              }
            }}
            error={Boolean(formErrors.role)}
            errorMessage={formErrors.role}
          />
        </div>

        <div className="ocpi-connections__role-form-actions">
          <MyButton label={t('ocpi:cancel')} buttonType="outline" onClickFunc={closeAddRoleModal} />
          <MyButton
            label={addingRole ? t('ocpi:adding') : t('ocpi:addRoleButton')}
            buttonType="primary"
            onClickFunc={handleAddRole}
            disableButton={addingRole}
          />
        </div>
      </div>
    );
  };

  interface ConnectionRow {
    id: string;
    partyId: string;
    countryCode: string;
    clientRole: string;
    version: string;
    status: string;
    raw: any;
    createdAt: string;
  }

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof ConnectionRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {ConnectionRow} currentRow passing as props
       * @returns {any} returning created date
       */
      customData: (currentRow: ConnectionRow) => (
        <span>{ConvertUTCtoDateTime(currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:clientRole'),
      accessorKey: 'clientRole' as keyof ConnectionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:partyId'),
      accessorKey: 'partyId' as keyof ConnectionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:countryCode'),
      accessorKey: 'countryCode' as keyof ConnectionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:version'),
      accessorKey: 'version' as keyof ConnectionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:registration'),
      accessorKey: 'status' as keyof ConnectionRow,
      sortAllow: false,
      /**
       * Renders the registration status badge.
       * @param {ConnectionRow} row - The connection row.
       * @returns {any} The status badge element.
       */
      customData: (row: ConnectionRow) => {
        let statusLabel = row.status;
        if (row.status === 'CONNECTED') {
          statusLabel = t('ocpi:connected');
        } else if (row.status === 'PENDING') {
          statusLabel = t('ocpi:pending');
        }

        return (
          <span className={`status-badge ${row.status.toLowerCase() || 'pending'}`}>
            {statusLabel}
          </span>
        );
      },
    },
    {
      label: t('ocpi:actions'),
      accessorKey: 'status' as keyof ConnectionRow,
      sortAllow: false,
      /**
       * Renders the actions for the connection.
       * @param {ConnectionRow} row - The connection row.
       * @returns {any} The actions element.
       */
      customData: (row: ConnectionRow) => (
        <div className="action-button-groups">
          <div
            className="action-button-items view-color"
            onClick={(event) => {
              event.stopPropagation();
              handleConnectionSuspend(row);
            }}
            role="presentation">
            {row.raw.suspended ? <ReconnectedIcon /> : <SuspendedIcon />}
          </div>
          <div
            className="action-buttons-wrap"
            role="presentation"
            onKeyDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteConnection(row.raw);
            }}>
            <DeleteIcon className="action-button delete" />
          </div>
        </div>
      ),
    },
    // {
    //   label: t('ocpi:actions'),
    //   accessorKey: 'id' as keyof ConnectionRow,
    //   sortAllow: false,
    //   hideWhenSidePanelOpen: true,
    //   /**
    //    * Renders the delete action for the connection. Viewing details happens via row click.
    //    * @param {ConnectionRow} row The connection row.
    //    * @returns {JSX.Element} The delete action button element.
    //    */
    //   customData: (row: ConnectionRow) => {
    //     const conn = row.raw;
    //     return (
    //       <div
    //         className="action-buttons-wrap"
    //         role="presentation"
    //         onKeyDown={(event) => event.stopPropagation()}
    //         onClick={(event) => {
    //           event.stopPropagation();
    //           handleDeleteConnection(conn);
    //         }}>
    //         <DeleteIcon className="action-button delete" />
    //       </div>
    //     );
    //   },
    // },
  ];

  const tableData: ConnectionRow[] = filteredConnections.map((conn) => {
    const activeRoleName = selectedRole?.role || activeProduct;
    const isWeSender = conn.senderRole?.toUpperCase() === activeRoleName?.toUpperCase();
    const partnerRole = isWeSender ? conn.role : conn.senderRole;
    const partnerPartyId = isWeSender ? conn.partyId : conn.senderPartyId;
    const partnerCountryCode = isWeSender ? conn.countryCode : conn.senderCountryCode;
    return {
      id: conn.id || conn._id,
      partyId: partnerPartyId || t('ocpi:unknown'),
      countryCode: partnerCountryCode || t('ocpi:unknown'),
      clientRole: partnerRole || t('ocpi:unknown'),
      version: '2.2.1',
      status: conn.status || 'PENDING',
      raw: conn,
      createdAt: conn.createdAt || conn.created_at || conn.created || '',
    };
  });

  return (
    <div className="connections-split-layout">
      {/* ── Left Pane: Roles ──────────────────────────────────────────────── */}
      <div className="roles-pane">
        <div className="pane-header">
          <div className="header-text">
            <h3>{t('ocpi:platformRoles')}</h3>
          </div>
          <MyButton
            label={t('ocpi:addRole')}
            buttonType="submit"
            hoverLabel={t('ocpi:addNewRoleTitle')}
            disableClass="ocpi-connections__add-role-btn"
            onClickFunc={openAddRoleModal}
            svgIcon={<PlusIcon />}
          />
        </div>

        <div className="pane-content">
          {loadingRoles ? (
            <div className="ocpi-connections__empty-state ocpi-connections__empty-state--roles-loading">
              {t('ocpi:loadingRoles')}
            </div>
          ) : (
            <div className="role-cards-list">
              {tenants.map((tenant) => {
                const roleConns = connections.filter((c) => {
                  const isSenderMatch =
                    c.senderRole?.toUpperCase() === tenant.role?.toUpperCase() &&
                    c.senderPartyId?.toUpperCase() === tenant.partyId?.toUpperCase() &&
                    c.senderCountryCode?.toUpperCase() === tenant.countryCode?.toUpperCase();

                  const isReceiverMatch =
                    c.role?.toUpperCase() === tenant.role?.toUpperCase() &&
                    c.partyId?.toUpperCase() === tenant.partyId?.toUpperCase() &&
                    c.countryCode?.toUpperCase() === tenant.countryCode?.toUpperCase();

                  const loopback = isLoopbackConnection(c);
                  if (loopback) {
                    return isSenderMatch;
                  }
                  return isSenderMatch || isReceiverMatch;
                });

                const totalCount = roleConns.length;

                const isSelected = selectedRole
                  ? (selectedRole.id && tenant.id && selectedRole.id === tenant.id) ||
                    (selectedRole._id && tenant._id && selectedRole._id === tenant._id) ||
                    (selectedRole.role === tenant.role &&
                      selectedRole.partyId === tenant.partyId &&
                      selectedRole.countryCode === tenant.countryCode)
                  : false;

                return (
                  <div
                    key={
                      tenant.id ||
                      tenant._id ||
                      `${tenant.role}-${tenant.countryCode}-${tenant.partyId}`
                    }
                    className={`role-card ${isSelected ? 'active' : ''}`}
                    onClick={() => handleSelectRole(tenant)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        handleSelectRole(tenant);
                      }
                    }}>
                    <div className="card-header">
                      <div className="header-icon-box">
                        <FacilityIcon width={22} height={22} />
                      </div>
                      <div className="header-title-wrap">
                        <h4>{tenant.role || t('ocpi:unknown')}</h4>
                        <div className="title-underline" />
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="body-timeline-wrapper">
                        <div className="info-row">
                          <div className="row-bare-icon">
                            <TagIcon width={18} height={18} />
                          </div>
                          <div className="row-content">
                            <span className="row-label">{t('ocpi:type')}</span>
                            <span className="row-value">{tenant.role}</span>
                          </div>
                        </div>

                        <div className="timeline-connector-line" />

                        <div className="info-row">
                          <div className="row-bare-icon">
                            <UserIcon width={18} height={18} />
                          </div>
                          <div className="row-content">
                            <span className="row-label">{t('ocpi:party')}</span>
                            <span className="row-value">
                              <strong className="party-highlight">
                                {tenant.partyId?.toUpperCase()}
                              </strong>
                              <span className="dot-separator">•</span>
                              <span className="country-text">
                                {t('ocpi:country')}: {tenant.countryCode?.toUpperCase()}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer">
                      <div className="connection-info">
                        <div className="footer-icon-box">
                          <LinkIcon width={16} height={16} />
                        </div>
                        <span className="connection-text">
                          {totalCount}{' '}
                          {t(
                            totalCount !== 1
                              ? 'ocpi:connectionCountPlural'
                              : 'ocpi:connectionCountSingular',
                          )}
                        </span>
                      </div>
                      <div className="arrow-box">
                        <NextArrowIcon width={16} height={16} />
                      </div>
                    </div>
                  </div>
                );
              })}
              {tenants.length === 0 && (
                <div className="ocpi-connections__empty-state ocpi-connections__empty-state--roles-empty">
                  {t('ocpi:noRolesFound')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Right Pane: Role Details & Connections ───────────────────────────────────────── */}
      <div className="connections-pane">
        {selectedRole ? (
          <>
            <div className="role-details-header">
              <div className="role-header-icon-box">
                <FacilityIcon width={26} height={26} />
              </div>
              <div className="role-header-teal-divider" aria-hidden="true" />
              <div className="role-info-section">
                <h3>{selectedRole.role || t('ocpi:unknown')}</h3>
                <p className="role-meta-row">
                  <span>
                    {t('ocpi:partyId')}: <strong>{selectedRole.partyId?.toUpperCase()}</strong>
                  </span>
                  <span className="role-meta-divider" aria-hidden="true" />
                  <span>
                    {t('ocpi:countryCode')}:{' '}
                    <strong>{selectedRole.countryCode?.toUpperCase()}</strong>
                  </span>
                </p>
              </div>
            </div>

            <div className="pane-content ocpi-connections__connections-table-wrap">
              <CustomTable
                version="v2"
                header={columns}
                data={tableData}
                limit={limit}
                page={page}
                totalPages={totalPages}
                totalResults={totalResults}
                loading={loadingConns}
                serialNo
                sortData={{ sortBy, sortField }}
                handleSortChange={handleSortFunction}
                pageLimitChange={pageLimitChange}
                filters={filters}
                filtersToDispatch={updateConnectionFilters}
                filterListLoading={filterListLoading}
                filterListToDispatch={fetchOcpiConnectionFilters}
                rowClickEvent={handleOpenConnectionModal}
                rowClickParamsKey="id"
                handleRefresh={handleRefresh}
                hoverLabel={t('ocpi:refresh')}
                refershCurrentPages={handleRefresh}
                sizeChangeButtonRequired
                rightTopContent={
                  <div className="role-actions-section">
                    {/* <button type="button" className="btn-outline">
                      <Eye size={16} /> {t('ocpi:editRole')}
                    </button>
                    <button
                      type="button"
                      className="btn-outline-danger"
                      onClick={() => handleDeleteRole(selectedRole)}>
                      <Trash2 size={16} /> {t('ocpi:deleteRole')}
                    </button> */}
                    <MyButton
                      label={t('ocpi:connectionLabel')}
                      buttonType="primary"
                      svgIcon={<LinkIcon height={16} width={16} />}
                      onClickFunc={() => setIsRegisterModalOpen(true)}
                    />
                    <MyButton
                      label={t('ocpi:operations')}
                      buttonType="outline"
                      svgIcon={<Settings height={16} width={16} />}
                      onClickFunc={() =>
                        navigate(`/ev-admin/ocpi/operations/${selectedRole.id || selectedRole._id}`)
                      }
                    />
                    {/* <MyButton
                      label={t('ocpi:configurations')}
                      buttonType="outline"
                      svgIcon={<SlidersHorizontal size={16} />}
                      onClickFunc={() => setIsConfigModalOpen(true)}
                    /> */}
                  </div>
                }
              />
            </div>
          </>
        ) : (
          <div className="ocpi-connections__empty-state">{t('ocpi:selectRolePrompt')}</div>
        )}
      </div>

      {/* Modals & Panels */}
      {isRegisterModalOpen && selectedRole && (
        <RegisterPartnerModal
          open={isRegisterModalOpen}
          onClose={() => setIsRegisterModalOpen(false)}
          onSuccess={() =>
            dispatch(
              fetchOcpiConnections({
                role: selectedRole.role,
                tenantId: selectedRole.id || selectedRole._id,
              }),
            )
          }
          ourPartyId={selectedRole.partyId || 'ENR'}
          ourCountryCode={selectedRole.countryCode || ''}
          ourRole={selectedRole.role || activeProduct || ''}
        />
      )}

      {isConfigModalOpen && selectedRole && (
        <AdditionalFieldsModal
          open={isConfigModalOpen}
          onClose={() => setIsConfigModalOpen(false)}
          role={selectedRole.role || activeProduct || ''}
          partyId={selectedRole.partyId || ''}
          countryCode={selectedRole.countryCode || ''}
        />
      )}

      {isAddRoleModalOpen && (
        <ModalBox
          open={isAddRoleModalOpen}
          title={t('ocpi:addNewRole')}
          content={renderAddRoleContent()}
          closeModal={closeAddRoleModal}
          header
          rightCorner=""
        />
      )}

      {/* Delete Role Confirmation Modal */}
      {showDeleteRoleModal && (
        <ModalBox
          open={showDeleteRoleModal}
          closeModal={() => {
            setShowDeleteRoleModal(false);
            setRoleToDelete(null);
          }}
          title={null}
          header={false}
          content={
            <DeleteWarning
              selectedRowId={roleToDelete?._id || roleToDelete?.id}
              title={t('ocpi:deleteRoleConfirmTitle')}
              description={t('ocpi:deleteRoleConfirmDescription', { role: roleToDelete?.role })}
              cancelButtonOnClick={() => {
                setShowDeleteRoleModal(false);
                setRoleToDelete(null);
              }}
              submitButtonOnClick={executeDeleteRole}
            />
          }
        />
      )}

      {/* Connection Suspend Confirmation Modal */}
      {connectionSuspend && (
        <ModalBox
          open={connectionSuspend}
          closeModal={() => {
            setShowConnectionSuspendModal(false);
            setConnectionToDelete(null);
          }}
          title={null}
          header={false}
          content={
            <DeleteWarning
              selectedRowId={connectionToDelete?._id || connectionToDelete?.id}
              title={connectionToDelete?.raw?.suspended ? t('ocpi:reConnectionConfirmTitle') : t('ocpi:suspendConnectionConfirmTitle')}
              description={connectionToDelete?.raw?.suspended ? t('ocpi:reConnectionConfirmDescription') : t('ocpi:suspendConnectionConfirmDescription')}
              cancelButtonOnClick={() => {
                setShowConnectionSuspendModal(false);
                setConnectionToDelete(null);
              }}
              submitButtonLabel={connectionToDelete?.raw?.suspended ? t('ocpi:reconnect') : t('ocpi:suspend')}
              submitButtonOnClick={executeSuspendConnection}
            />
          }
        />
      )}

      {/* Delete Connection Confirmation Modal */}
      {showDeleteConnectionModal && (
        <ModalBox
          open={showDeleteConnectionModal}
          closeModal={() => {
            setShowDeleteConnectionModal(false);
            setConnectionToDelete(null);
          }}
          title={null}
          header={false}
          content={
            <DeleteWarning
              selectedRowId={connectionToDelete?._id || connectionToDelete?.id}
              title={t('ocpi:deleteConnectionConfirmTitle')}
              description={t('ocpi:deleteConnectionConfirmDescription')}
              cancelButtonOnClick={() => {
                setShowDeleteConnectionModal(false);
                setConnectionToDelete(null);
              }}
              submitButtonOnClick={executeDeleteConnection}
            />
          }
        />
      )}

      {/* Connection Details Side Panel */}
      <SidePaneModel
        open={Boolean(selectedConnection)}
        closePanel={() => setSelectedConnectionId(null)}
        title={t('ocpi:connectionLabel')}
        content={
          <ConnectionSidePane
            connection={selectedConnection}
            activeRole={selectedRole?.role || activeProduct || ''}
            // onAuthorize={() =>
            //   dispatch(authorizeOcpiConnection(selectedConnection._id || selectedConnection.id))
            // }
          />
        }
      />
    </div>
  );
}

export default OCPIConnections;
